import React, { useRef, useEffect, useState, useCallback, ReactNode } from 'react';
import { motion, AnimatePresence, Variants } from 'framer-motion';

// Icons
import CameraIcon from './icons/CameraIcon';
import AlertTriangleIcon from './icons/AlertTriangleIcon';
import RefreshCwIcon from './icons/RefreshCwIcon'; // Used for Retake
import CheckIcon from './icons/CheckIcon';
import ArrowLeftIcon from './icons/ArrowLeftIcon';

// --- INLINE ICON DEFINITIONS ---
const SwitchCameraIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
        <path d="M11 19H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h5" />
        <path d="M13 5h7a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-5" />
        <circle cx="12" cy="12" r="3" />
        <path d="M18 22l-3-3 3-3" />
        <path d="M6 2l3 3-3 3" />
    </svg>
);

// --- PROPS INTERFACE ---
interface CameraCaptureProps {
    onCapture: (file: File) => void;
    onCancel: () => void;
}

// --- DSD OVERLAY COMPONENT ---
const DSDOverlay: React.FC<{ isReview?: boolean }> = ({ isReview = false }) => {
    const tealColor = '#2dd4bf'; // A vibrant teal
    const baseOpacity = isReview ? 0.9 : 0.75;

    const draw: Variants = {
        hidden: { pathLength: 0, opacity: 0 },
        visible: (i: number) => ({
            pathLength: 1,
            opacity: 1,
            transition: {
                pathLength: { delay: i * 0.1, type: 'spring', duration: 1.5, bounce: 0 },
                opacity: { delay: i * 0.1, duration: 0.1 },
            },
        }),
    };

    const fadeIn = {
        hidden: { opacity: 0 },
        visible: (i: number) => ({
            opacity: baseOpacity,
            transition: { delay: i * 0.1 + 0.5, duration: 0.5 }
        })
    };

    return (
        <motion.svg
            className="absolute inset-0 w-full h-full pointer-events-none"
            viewBox="0 0 300 300"
            preserveAspectRatio="xMidYMid meet"
            initial="hidden"
            animate="visible"
        >
            <defs>
                {/* A subtle glow for the glass effect */}
                <filter id="glass-glow" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur in="SourceGraphic" stdDeviation="2" result="blur" />
                    <feComposite in="SourceGraphic" in2="blur" operator="over" />
                </filter>
                {/* A vibrant gradient for the arc */}
                <linearGradient id="tealArcGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor={tealColor} stopOpacity="0.1" />
                    <stop offset="50%" stopColor={tealColor} stopOpacity="1" />
                    <stop offset="100%" stopColor={tealColor} stopOpacity="0.1" />
                </linearGradient>
            </defs>

            {/* Ultra-thin corner brackets */}
            <motion.g stroke="white" strokeWidth="0.75" initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.1 } } }}>
                <motion.path d="M 60 40 L 40 40 L 40 60" fill="none" variants={draw} />
                <motion.path d="M 240 40 L 260 40 L 260 60" fill="none" variants={draw} />
                <motion.path d="M 60 260 L 40 260 L 40 240" fill="none" variants={draw} />
                <motion.path d="M 240 260 L 260 260 L 260 240" fill="none" variants={draw} />
            </motion.g>

            {/* Ultra-thin central dashed crosshair */}
            <motion.line
                x1="150" y1="40" x2="150" y2="260" // Vertical line
                stroke="white"
                strokeWidth={0.5}
                strokeDasharray="2 3"
                variants={fadeIn}
                custom={4}
            />
            <motion.line
                x1="40" y1="150" x2="260" y2="150" // Horizontal line (pupils)
                stroke="white"
                strokeWidth={0.5}
                strokeDasharray="2 3"
                variants={fadeIn}
                custom={5}
            />

            {/* Sophisticated Smile Arc with glassmorphism effect */}
            <motion.path
                d="M 90 200 Q 150 220 210 200"
                stroke="url(#tealArcGradient)"
                strokeWidth="2"
                fill="none"
                filter="url(#glass-glow)" // Apply the glass glow
                variants={draw}
                custom={6}
                style={{ opacity: isReview ? 1 : 0.9 }}
            />

            {/* Modern, clean sans-serif text */}
            <motion.g variants={fadeIn} custom={7}>
                <text x="150" y="230" dominantBaseline="middle" textAnchor="middle" fill={tealColor} opacity={isReview ? 0.9 : 0.8} fontSize="8" fontWeight="500" letterSpacing="1.5" style={{ fontFamily: 'Inter, sans-serif' }}>SMILE ARC</text>
            </motion.g>
        </motion.svg>
    );
};

// --- CROP OVERLAY COMPONENT ---
const CropOverlay: React.FC<{ cropRect: { x: number; y: number; width: number; height: number; } }> = ({ cropRect }) => {
    if (cropRect.width === 0) return null;
    return (
        <div
            className="absolute shadow-[0_0_0_9999px_rgba(0,0,0,0.6)] pointer-events-none rounded-lg"
            style={{
                top: `${cropRect.y}px`,
                left: `${cropRect.x}px`,
                width: `${cropRect.width}px`,
                height: `${cropRect.height}px`,
            }}
        />
    );
};


// --- CAPTURE STEP COMPONENT ---
const CaptureScreen: React.FC<{
    onCapture: () => void;
    onCancel: () => void;
    onSwitchCamera: () => void;
    showSwitchCamera: boolean;
    videoRef: React.RefObject<HTMLVideoElement>;
    bgVideoRef: React.RefObject<HTMLVideoElement>;
    cropRect: { x: number; y: number; width: number; height: number; };
}> = ({ onCapture, onCancel, onSwitchCamera, showSwitchCamera, videoRef, bgVideoRef, cropRect }) => (
    <motion.div
        key="capture"
        className="w-full h-full relative bg-black"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
    >
        {/* Real-time lighted shadow background */}
        <video
            ref={bgVideoRef}
            autoPlay playsInline muted
            className="absolute inset-0 w-full h-full object-cover scale-x-[-1] transform scale-[1.3] blur-3xl brightness-50"
            style={{
                maskImage: 'radial-gradient(ellipse 80% 70% at 50% 50%, black 40%, transparent 100%)',
                WebkitMaskImage: 'radial-gradient(ellipse 80% 70% at 50% 50%, black 40%, transparent 100%)'
            }}
        />

        {/* Main camera feed */}
        <video
            ref={videoRef}
            autoPlay playsInline muted
            className="absolute inset-0 w-full h-full object-contain scale-x-[-1]"
        />

        {/* Dimming overlay */}
        <CropOverlay cropRect={cropRect} />

        {/* Alignment guides, constrained to crop area */}
        <div className="absolute pointer-events-none" style={{
            top: `${cropRect.y}px`,
            left: `${cropRect.x}px`,
            width: `${cropRect.width}px`,
            height: `${cropRect.height}px`,
        }}>
            <DSDOverlay />
        </div>


        {/* UI Controls */}
        <div className="absolute top-6 right-6 z-20 flex flex-col gap-4">
            <button onClick={onCancel} className="p-3 text-zinc-300 bg-black/40 backdrop-blur-sm rounded-full hover:bg-black/60 hover:text-white transition-colors" aria-label="Close Camera">
                <ArrowLeftIcon className="w-6 h-6" />
            </button>

            {showSwitchCamera && (
                <button onClick={onSwitchCamera} className="p-3 text-zinc-300 bg-black/40 backdrop-blur-sm rounded-full hover:bg-black/60 hover:text-white transition-colors" aria-label="Switch Camera">
                    <SwitchCameraIcon className="w-6 h-6" />
                </button>
            )}
        </div>

        <div className="absolute bottom-8 sm:bottom-12 flex items-center justify-center w-full z-10">
            <motion.button
                onClick={onCapture}
                aria-label="Capture photo"
                className="w-20 h-20 rounded-full border-[6px] border-white/90 bg-transparent transition-colors hover:border-white active:bg-white/20"
                whileTap={{ scale: 0.9 }}
                transition={{ type: 'spring', stiffness: 500, damping: 15 }}
            />
        </div>
    </motion.div>
);

// --- REVIEW STEP COMPONENT (REDESIGNED) ---
const ReviewScreen: React.FC<{ image: string | null; onConfirm: () => void; onRetake: () => void; }> = ({ image, onConfirm, onRetake }) => (
    <motion.div
        key="review"
        className="w-full h-full flex flex-col p-6 sm:p-8"
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.98 }}
        transition={{ duration: 0.4, ease: 'easeInOut' }}
    >
        {/* Header */}
        <header className="text-center flex-shrink-0">
            <h1 className="text-3xl font-bold text-white">Confirm Capture</h1>
            <p className="text-zinc-400 mt-1">Ensure the smile arc and midline are correctly aligned.</p>
        </header>

        {/* Image Preview (takes up most space) */}
        <main className="flex-1 flex items-center justify-center my-4 min-h-0">
            <div className="relative w-full max-w-md aspect-square rounded-xl overflow-hidden bg-zinc-900 border border-zinc-700 shadow-2xl shadow-black/50">
                {image && <img src={image} alt="Captured smile" className="w-full h-full object-contain" />}
                <DSDOverlay isReview />
            </div>
        </main>

        {/* Action Footer */}
        <footer className="flex-shrink-0 w-full max-w-md mx-auto">
            <div className="flex flex-col gap-3">
                <motion.button
                    onClick={onConfirm}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 rounded-xl text-white bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 transition-all duration-300 font-semibold text-lg shadow-lg shadow-teal-500/30"
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 15 }}
                >
                    <CheckIcon className="w-6 h-6" />
                    Confirm & Proceed
                </motion.button>
                <motion.button
                    onClick={onRetake}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-medium text-zinc-400 hover:text-white hover:bg-zinc-800/50 transition-colors"
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 15 }}
                >
                    <RefreshCwIcon className="w-4 h-4" />
                    Retake Photo
                </motion.button>
            </div>
        </footer>
    </motion.div>
);

// --- MAIN COMPONENT ---
const CameraCapture: React.FC<CameraCaptureProps> = ({ onCapture, onCancel }) => {
    const [step, setStep] = useState<'capture' | 'review'>('capture');
    const [error, setError] = useState<string | null>(null);
    const [capturedImage, setCapturedImage] = useState<string | null>(null);
    const [cropRect, setCropRect] = useState({ x: 0, y: 0, width: 0, height: 0 });

    // Camera Device State
    const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
    const [currentDeviceIndex, setCurrentDeviceIndex] = useState<number>(0);

    const videoRef = useRef<HTMLVideoElement>(null);
    const bgVideoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    const calculateLayout = useCallback(() => {
        const video = videoRef.current;
        if (!video || !video.videoWidth || !video.videoHeight) return;
        const container = video.parentElement;
        if (!container) return;

        // 1. Calculate displayed video dimensions (object-contain)
        const containerAR = container.clientWidth / container.clientHeight;
        const videoAR = video.videoWidth / video.videoHeight;
        let videoBoxWidth, videoBoxHeight, videoBoxX, videoBoxY;

        if (videoAR > containerAR) { // letterboxed
            videoBoxWidth = container.clientWidth;
            videoBoxHeight = videoBoxWidth / videoAR;
            videoBoxX = 0;
            videoBoxY = (container.clientHeight - videoBoxHeight) / 2;
        } else { // pillarboxed
            videoBoxHeight = container.clientHeight;
            videoBoxWidth = videoBoxHeight * videoAR;
            videoBoxX = (container.clientWidth - videoBoxWidth) / 2;
            videoBoxY = 0;
        }

        // 2. Calculate crop rect relative to container
        const CROP_AR = 1 / 1;
        const effectiveVideoAR = videoBoxWidth / videoBoxHeight;

        let newCropRect = { x: 0, y: 0, width: 0, height: 0 };
        if (CROP_AR < effectiveVideoAR) { // Video wider than crop, crop sides
            newCropRect.height = videoBoxHeight;
            newCropRect.width = newCropRect.height * CROP_AR;
            newCropRect.x = videoBoxX + (videoBoxWidth - newCropRect.width) / 2;
            newCropRect.y = videoBoxY;
        } else { // Video taller than crop, crop top/bottom
            newCropRect.width = videoBoxWidth;
            newCropRect.height = newCropRect.width / CROP_AR;
            newCropRect.x = videoBoxX;
            newCropRect.y = videoBoxY + (videoBoxHeight - newCropRect.height) / 2;
        }
        setCropRect(newCropRect);
    }, []);

    // 1. Enumerate Devices
    useEffect(() => {
        const enumerate = async () => {
            try {
                // First ask for permission to labels are available
                const stream = await navigator.mediaDevices.getUserMedia({ video: true });
                stream.getTracks().forEach(track => track.stop()); // Stop immediately

                const allDevices = await navigator.mediaDevices.enumerateDevices();
                const videoDevices = allDevices.filter(d => d.kind === 'videoinput');
                setDevices(videoDevices);

                // Try to find a user-facing camera to start with
                const userFacingIndex = videoDevices.findIndex(d => d.label.toLowerCase().includes('user') || d.label.toLowerCase().includes('front'));
                if (userFacingIndex !== -1) {
                    setCurrentDeviceIndex(userFacingIndex);
                }
            } catch (err) {
                console.error("Error enumerating devices:", err);
                // Permissions might be denied, error will be handled in getCamera
            }
        };
        enumerate();
    }, []);

    // 2. Initialize Camera
    useEffect(() => {
        if (step !== 'capture') {
            setCropRect({ x: 0, y: 0, width: 0, height: 0 }); // Reset on step change
            return;
        }

        let stream: MediaStream | null = null;
        let active = true;

        const getCamera = async () => {
            if (!active) return;

            try {
                const constraints: MediaStreamConstraints = {
                    video: {
                        width: { ideal: 1920 },
                        height: { ideal: 1080 },
                        deviceId: devices.length > 0 ? { exact: devices[currentDeviceIndex].deviceId } : undefined,
                        facingMode: devices.length === 0 ? 'user' : undefined // Fallback if enumeration failed or empty
                    }
                };

                const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);

                if (active && videoRef.current && bgVideoRef.current) {
                    videoRef.current.srcObject = mediaStream;
                    bgVideoRef.current.srcObject = mediaStream;
                    stream = mediaStream;
                    setError(null);
                } else {
                    mediaStream.getTracks().forEach(track => track.stop());
                }
            } catch (err) {
                console.error("Error accessing camera:", err);
                // More friendly error messages
                if (err instanceof DOMException) {
                    if (err.name === 'NotAllowedError') {
                        setError("Camera access denied. Please check your browser permissions.");
                    } else if (err.name === 'NotFoundError') {
                        setError("No camera device found.");
                    } else if (err.name === 'NotReadableError') {
                        setError("Camera is currently in use by another application.");
                    } else {
                        setError("Could not access camera. " + err.message);
                    }
                } else {
                    setError("An unknown error occurred while accessing the camera.");
                }
            }
        };

        getCamera();

        const video = videoRef.current;
        if (video) {
            const handleMetadata = () => { setTimeout(calculateLayout, 100); };
            video.addEventListener('loadedmetadata', handleMetadata);
            window.addEventListener('resize', calculateLayout);
            return () => {
                video.removeEventListener('loadedmetadata', handleMetadata);
                window.removeEventListener('resize', calculateLayout);
                active = false;
                if (stream) stream.getTracks().forEach(track => track.stop());
            };
        }
    }, [step, calculateLayout, devices, currentDeviceIndex]);

    const handleCapture = useCallback(() => {
        if (!videoRef.current || !canvasRef.current) return;
        const video = videoRef.current;
        const canvas = canvasRef.current;
        const { videoWidth, videoHeight } = video;

        if (!videoWidth || !videoHeight) return;

        const containerAR = 1;
        // Set high resolution for the capture
        canvas.width = 1080;
        canvas.height = 1080;

        let sx = 0, sy = 0, sWidth = videoWidth, sHeight = videoHeight;
        const videoAR = videoWidth / videoHeight;

        if (containerAR < videoAR) {
            sWidth = videoHeight * containerAR;
            sx = (videoWidth - sWidth) / 2;
        } else if (containerAR > videoAR) {
            sHeight = videoWidth / containerAR;
            sy = (videoHeight - sHeight) / 2;
        }

        const context = canvas.getContext('2d');
        if (context) {
            context.translate(canvas.width, 0);
            context.scale(-1, 1);
            context.drawImage(video, sx, sy, sWidth, sHeight, 0, 0, canvas.width, canvas.height);
            const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
            setCapturedImage(dataUrl);
            setStep('review');
        }
    }, []);

    const handleRetake = () => {
        setCapturedImage(null);
        setStep('capture');
    };

    const handleConfirm = useCallback(() => {
        if (!capturedImage) return;
        fetch(capturedImage)
            .then(res => res.blob())
            .then(blob => {
                const file = new File([blob], `futura-smile-capture-${Date.now()}.jpg`, { type: 'image/jpeg' });
                onCapture(file);
            });
    }, [capturedImage, onCapture]);

    const handleSwitchCamera = () => {
        if (devices.length < 2) return;
        setCurrentDeviceIndex(prev => (prev + 1) % devices.length);
    };

    if (error) {
        return (
            <motion.div
                className="fixed inset-0 z-50 bg-black flex flex-col items-center justify-center p-8"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            >
                <div className="bg-red-950/50 border border-red-500/20 p-8 rounded-2xl max-w-lg text-center shadow-lg shadow-red-950/50" role="alert">
                    <div className="flex justify-center mb-4">
                        <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center border border-red-500/20">
                            <AlertTriangleIcon className="w-6 h-6 text-red-500" />
                        </div>
                    </div>
                    <h2 className="font-bold text-2xl text-red-400">Camera Unavailable</h2>
                    <p className="text-zinc-400 mt-2">{error}</p>
                    <div className="mt-6 flex gap-4 justify-center">
                        <button onClick={onCancel} className="px-6 py-2 rounded-lg bg-zinc-800 text-zinc-200 font-semibold hover:bg-zinc-700 transition-colors">Return</button>
                        {/* Add a retry button to re-trigger enumeration/permission path */}
                        <button onClick={() => window.location.reload()} className="px-6 py-2 rounded-lg bg-red-900/40 text-red-200 font-semibold hover:bg-red-900/60 transition-colors">Retry</button>
                    </div>
                </div>
            </motion.div>
        );
    }

    return (
        <motion.div
            className="fixed inset-0 z-50 bg-black flex flex-col items-center justify-center"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        >
            <AnimatePresence mode="wait">
                {step === 'capture' && (
                    <CaptureScreen
                        onCapture={handleCapture}
                        onCancel={onCancel}
                        onSwitchCamera={handleSwitchCamera}
                        showSwitchCamera={devices.length > 1}
                        videoRef={videoRef}
                        bgVideoRef={bgVideoRef}
                        cropRect={cropRect}
                    />
                )}
                {step === 'review' && (
                    <ReviewScreen
                        image={capturedImage}
                        onConfirm={handleConfirm}
                        onRetake={handleRetake}
                    />
                )}
            </AnimatePresence>
            <canvas ref={canvasRef} className="hidden" />
        </motion.div>
    );
};

export default CameraCapture;